import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { Card, Button, Spinner, Alert, Form, Row, Col, Table, Badge, Modal } from 'react-bootstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPlus, faPlay, faTrash, faEdit, faMagic, faCodeBranch } from '@fortawesome/free-solid-svg-icons'
import { useAdminMunicipality } from 'api/hooks/use-admin-municipalities'
import {
  useCustomAnalytics,
  useCreateCustomAnalytic,
  useUpdateCustomAnalytic,
  useDeleteCustomAnalytic,
  useCreateCustomAnalyticRun,
  useComposablePredicates,
  useSectionColumns,
  useGenerateCustomAnalyticRecipe,
  useMergeCustomAnalytics
} from 'api/hooks/use-custom-analytics'
import RecipeBuilder from './RecipeBuilder'
import RunStatusBanner from './RunStatusBanner'
import RecipeBuilderContext from './RecipeBuilderContext'
import { defaultRecipe, validateRecipe, ensureKeys, recipeForApi } from './recipe-helpers'

const MunicipalityCustomAnalytics = () => {
  const { id } = useParams()
  const { data: municipality, isLoading: muniLoading, error: muniError } = useAdminMunicipality({ id })
  const { data: predicateRegistry, isLoading: registryLoading } = useComposablePredicates()

  // The muni's sections the API says composable analytics can run against.
  const composableSections = useMemo(() => {
    if (!municipality?.municipality_sections) return []
    return municipality.municipality_sections
      .filter((ms) => ms.section?.is_composable)
      .map((ms) => ({ id: ms.section.id, abbreviation: ms.section.abbreviation, name: ms.section.name }))
  }, [municipality])

  const [selectedSectionId, setSelectedSectionId] = useState('')

  // Default-select the first composable section once the muni loads.
  useEffect(() => {
    if (!selectedSectionId && composableSections.length > 0) {
      setSelectedSectionId(composableSections[0].id)
    }
  }, [composableSections, selectedSectionId])

  const { data: analyticsData, isLoading: analyticsLoading } = useCustomAnalytics({
    municipalityId: id,
    sectionId: selectedSectionId,
    options: { enabled: !!id && !!selectedSectionId }
  })

  const { data: columnsData, isLoading: columnsLoading } = useSectionColumns({
    municipalityId: id,
    sectionId: selectedSectionId
  })

  const recipeBuilderContextValue = useMemo(
    () => ({
      columns: columnsData?.columns || [],
      isLoading: columnsLoading,
      auditColumns: columnsData?.audit_columns || null
    }),
    [columnsData, columnsLoading]
  )

  const createMutation = useCreateCustomAnalytic({ municipalityId: id })
  const updateMutation = useUpdateCustomAnalytic({ municipalityId: id })
  const deleteMutation = useDeleteCustomAnalytic({ municipalityId: id })
  const runMutation = useCreateCustomAnalyticRun({ municipalityId: id })
  const generateRecipeMutation = useGenerateCustomAnalyticRecipe({ municipalityId: id })
  const mergeMutation = useMergeCustomAnalytics({ municipalityId: id })

  const [editingAnalytic, setEditingAnalytic] = useState(null)
  const [showModal, setShowModal] = useState(false)
  const [activeRunId, setActiveRunId] = useState(null)
  const [formError, setFormError] = useState(null)
  const [aiDescription, setAiDescription] = useState('')
  const [aiError, setAiError] = useState(null)
  const [selectedIds, setSelectedIds] = useState([])
  const [showMergeModal, setShowMergeModal] = useState(false)
  const [mergeForm, setMergeForm] = useState({ name: '', description: '' })
  const [mergeError, setMergeError] = useState(null)

  // Identifies the current edit session (one open-to-close cycle of the
  // recipe modal). generateRecipeMutation's LLM call can take a while;
  // without this, a stale response arriving after the admin cancels and
  // opens a *different* analytic would silently overwrite it (editingAnalytic
  // is shared state and isn't reset on close). Bumped on every open, close,
  // and successful save; any in-flight mutation callback checks its captured
  // session against the current value before touching state.
  const editSessionRef = useRef(0)

  if (muniLoading || registryLoading) {
    return (
      <Spinner animation="border" role="status">
        <span className="visually-hidden">Loading…</span>
      </Spinner>
    )
  }
  if (muniError) return <Alert variant="danger">Failed to load municipality.</Alert>

  // Ends the current edit session — bump the ref so any in-flight
  // generate/save callback from this session becomes stale and is ignored.
  const closeModal = () => {
    editSessionRef.current += 1
    setShowModal(false)
  }

  const openCreate = () => {
    editSessionRef.current += 1
    setEditingAnalytic({ name: '', description: '', description_template: '', recipe: defaultRecipe() })
    setFormError(null)
    setAiDescription('')
    setAiError(null)
    setShowModal(true)
  }

  const openEdit = (analytic) => {
    editSessionRef.current += 1
    setEditingAnalytic({ ...analytic, recipe: ensureKeys(analytic.recipe) })
    setFormError(null)
    setAiDescription('')
    setAiError(null)
    setShowModal(true)
  }

  const handleGenerateRecipe = () => {
    setAiError(null)
    setFormError(null)
    const session = editSessionRef.current
    generateRecipeMutation.mutate(
      { section_id: selectedSectionId, description: aiDescription },
      {
        onSuccess: (data) => {
          if (session !== editSessionRef.current) return // stale — a different (or no) session is now open
          setEditingAnalytic((prev) => ({
            ...prev,
            description: prev.description || aiDescription,
            recipe: ensureKeys(data.recipe)
          }))
        },
        onError: (err) => {
          if (session !== editSessionRef.current) return // stale — don't paint an error over a new session
          setAiError(err?.response?.data?.message || err?.response?.data?.error || err.message)
        }
      }
    )
  }

  const handleSave = () => {
    setFormError(null)
    const errors = validateRecipe(editingAnalytic.recipe, predicateRegistry?.predicates)
    if (errors.length > 0) {
      setFormError(`${errors.length} issue(s) — see warnings above.`)
      return
    }

    const payload = {
      name: editingAnalytic.name,
      description: editingAnalytic.description,
      description_template: editingAnalytic.description_template,
      recipe: recipeForApi(editingAnalytic.recipe, predicateRegistry?.predicates, columnsData?.audit_columns)
    }
    const session = editSessionRef.current
    if (editingAnalytic.id) {
      updateMutation.mutate(
        { id: editingAnalytic.id, ...payload },
        {
          onSuccess: () => closeModal(),
          onError: (err) => {
            if (session !== editSessionRef.current) return // stale — the modal moved on to a new session
            setFormError(err?.response?.data?.error || err.message)
          }
        }
      )
    } else {
      createMutation.mutate(
        { ...payload, section_id: selectedSectionId },
        {
          onSuccess: () => closeModal(),
          onError: (err) => {
            if (session !== editSessionRef.current) return // stale — the modal moved on to a new session
            setFormError(err?.response?.data?.error || err.message)
          }
        }
      )
    }
  }

  const handleDelete = (analytic) => {
    if (!window.confirm(`Delete custom analytic "${analytic.name}"?`)) return
    deleteMutation.mutate(analytic.id)
  }

  const handleRun = () => {
    runMutation.mutate(
      { section_id: selectedSectionId },
      { onSuccess: (data) => setActiveRunId(data.id) }
    )
  }

  const toggleSelected = (analyticId) => {
    setSelectedIds((prev) =>
      prev.includes(analyticId) ? prev.filter((x) => x !== analyticId) : [...prev, analyticId]
    )
  }

  const openMerge = () => {
    setMergeForm({ name: '', description: '' })
    setMergeError(null)
    setShowMergeModal(true)
  }

  const handleMerge = () => {
    setMergeError(null)
    mergeMutation.mutate(
      { analytic_ids: selectedIds, name: mergeForm.name, description: mergeForm.description },
      {
        onSuccess: () => {
          setShowMergeModal(false)
          setSelectedIds([])
        },
        onError: (err) =>
          setMergeError(err?.response?.data?.message || err?.response?.data?.error || err.message)
      }
    )
  }

  const analytics = analyticsData?.custom_analytics || []
  const noSections = composableSections.length === 0

  return (
    <div>
      <div className="bg-white border-bottom mb-4 py-3" style={{ margin: '-1rem -1rem 1rem -1rem', padding: '1rem' }}>
        <div className="d-flex justify-content-between align-items-center">
          <h3 className="mb-0">{municipality?.name} — Custom Analytics</h3>
          <Button as={Link} to={`/admin/municipalities/${id}`} variant="light" className="text-primary" size="sm">
            Back to Municipality
          </Button>
        </div>
      </div>

      {noSections && (
        <Alert variant="warning">
          This municipality has no composable sections assigned. Custom analytics
          run against modules like P-Card, AP, Purchasing, etc.
        </Alert>
      )}

      {!noSections && (
        <>
          <Row className="mb-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label className="small text-muted">Module</Form.Label>
                <Form.Select
                  data-testid="module-select"
                  value={selectedSectionId}
                  onChange={(e) => {
                    setSelectedSectionId(e.target.value)
                    setActiveRunId(null)
                    setSelectedIds([])
                  }}
                >
                  {composableSections.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name} ({s.abbreviation})
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={6} className="d-flex align-items-end justify-content-end">
              <Button variant="primary" onClick={openCreate} className="me-2">
                <FontAwesomeIcon icon={faPlus} className="me-1" />
                New recipe
              </Button>
              <Button
                variant="light"
                className="text-primary me-2"
                onClick={openMerge}
                disabled={selectedIds.length < 2}
              >
                <FontAwesomeIcon icon={faCodeBranch} className="me-1" />
                Merge selected ({selectedIds.length})
              </Button>
              <Button
                variant="success"
                onClick={handleRun}
                disabled={analytics.length === 0 || runMutation.isLoading}
              >
                <FontAwesomeIcon icon={faPlay} className="me-1" />
                Run analytics
              </Button>
            </Col>
          </Row>

          {activeRunId && (
            <RunStatusBanner municipalityId={id} runId={activeRunId} />
          )}

          {analyticsLoading ? (
            <Spinner animation="border" />
          ) : analytics.length === 0 ? (
            <Card body className="text-center text-muted">
              No custom analytics for this module yet. Click "New recipe" to build one.
            </Card>
          ) : (
            <Card>
              <Table className="mb-0" responsive>
                <thead>
                  <tr>
                    <th style={{ width: 32 }} />
                    <th>Key</th>
                    <th>Name</th>
                    <th>Description</th>
                    <th style={{ width: 140 }} />
                  </tr>
                </thead>
                <tbody>
                  {analytics.map((a) => (
                    <tr key={a.id}>
                      <td>
                        <Form.Check
                          type="checkbox"
                          aria-label={`Select ${a.key}`}
                          checked={selectedIds.includes(a.id)}
                          onChange={() => toggleSelected(a.id)}
                        />
                      </td>
                      <td><code>{a.key}</code></td>
                      <td>{a.name}</td>
                      <td className="text-muted small">{a.description}</td>
                      <td className="text-end">
                        <Button variant="light" className="text-primary me-1" size="sm" onClick={() => openEdit(a)}>
                          <FontAwesomeIcon icon={faEdit} />
                        </Button>
                        <Button variant="light" className="text-danger" size="sm" onClick={() => handleDelete(a)}>
                          <FontAwesomeIcon icon={faTrash} />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card>
          )}
        </>
      )}

      <Modal show={showModal} onHide={closeModal} size="xl" backdrop="static">
        <Modal.Header closeButton>
          <Modal.Title>
            {editingAnalytic?.id ? `Edit ${editingAnalytic.key}` : 'New custom analytic'}
            {editingAnalytic?.id && <Badge bg="light" text="primary" className="ms-2">{editingAnalytic.key}</Badge>}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {formError && <Alert variant="danger">{formError}</Alert>}
          <Card bg="light" className="mb-3">
            <Card.Body>
              <Form.Label className="d-flex align-items-center small text-muted mb-2">
                <FontAwesomeIcon icon={faMagic} className="me-2 text-primary" />
                Describe the analytic in plain English — we'll draft the recipe for you to refine.
              </Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={aiDescription}
                placeholder="e.g. weekend transactions over $5,000 paid to a new vendor"
                onChange={(e) => setAiDescription(e.target.value)}
                disabled={generateRecipeMutation.isLoading}
              />
              {aiError && <Alert variant="danger" className="mt-2 mb-0 py-2 small">{aiError}</Alert>}
              <div className="mt-2 d-flex justify-content-end">
                <Button
                  variant="primary"
                  size="sm"
                  onClick={handleGenerateRecipe}
                  disabled={!aiDescription.trim() || generateRecipeMutation.isLoading}
                >
                  {generateRecipeMutation.isLoading ? (
                    <><Spinner animation="border" size="sm" className="me-1" /> Generating…</>
                  ) : (
                    <><FontAwesomeIcon icon={faMagic} className="me-1" /> Generate recipe</>
                  )}
                </Button>
              </div>
            </Card.Body>
          </Card>
          {editingAnalytic && (
            <RecipeBuilderContext.Provider value={recipeBuilderContextValue}>
              <RecipeBuilder
                value={editingAnalytic}
                registry={predicateRegistry}
                onChange={setEditingAnalytic}
              />
            </RecipeBuilderContext.Provider>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" className="text-primary" onClick={closeModal}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={createMutation.isLoading || updateMutation.isLoading}
          >
            {(createMutation.isLoading || updateMutation.isLoading) ? 'Saving…' : 'Save'}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showMergeModal} onHide={() => setShowMergeModal(false)} backdrop="static">
        <Modal.Header closeButton>
          <Modal.Title>Merge {selectedIds.length} analytics</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {mergeError && <Alert variant="danger">{mergeError}</Alert>}
          <p className="small text-muted">
            Creates one new analytic that flags a transaction when <strong>any</strong> of the
            selected analytics would. The originals stay active and unchanged.
          </p>
          <ul className="small">
            {analytics.filter((a) => selectedIds.includes(a.id)).map((a) => (
              <li key={a.id}><code>{a.key}</code> — {a.name}</li>
            ))}
          </ul>
          <Form.Group className="mb-3">
            <Form.Label className="small text-muted">Name</Form.Label>
            <Form.Control
              value={mergeForm.name}
              placeholder="Leave blank to auto-generate a key"
              onChange={(e) => setMergeForm((f) => ({ ...f, name: e.target.value }))}
            />
          </Form.Group>
          <Form.Group>
            <Form.Label className="small text-muted">Description</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={mergeForm.description}
              placeholder="Leave blank to default to the merged source keys"
              onChange={(e) => setMergeForm((f) => ({ ...f, description: e.target.value }))}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" className="text-primary" onClick={() => setShowMergeModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleMerge} disabled={mergeMutation.isLoading}>
            {mergeMutation.isLoading ? 'Merging…' : 'Merge'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  )
}

export default MunicipalityCustomAnalytics
